"use client";

import { useRef } from "react";
import { ADSENSE_CLIENT, ADSENSE_SLOTS } from "@/lib/adsense";
import { useAdSensePush } from "@/hooks/useAdSensePush";
import { useIsDesktop } from "@/hooks/useViewport";

interface AdSenseSidebarProps {
  adSlot?: string;
}

export default function AdSenseSidebar({ adSlot }: AdSenseSidebarProps) {
  // 반응형 세로 슬롯 우선, 미발급(env 미설정)이면 레거시 sidebar 슬롯으로 폴백(JCG-2194).
  // AdSense 는 코드 레벨 format(data-ad-format="auto" + full-width-responsive)을 따르므로
  // 어느 슬롯이든 반응형으로 렌더된다(AdSenseInlineProblem 선례와 동일).
  const slot = adSlot ?? ADSENSE_SLOTS.sidebarResponsive ?? ADSENSE_SLOTS.sidebar;
  // 사이드바 광고는 데스크톱(lg+)에서만 노출. CSS 숨김이 아닌 조건부 렌더로
  // 모바일에 hidden ins가 남아 positional 큐를 오염시키는 것을 방지(JCG-2057).
  const isDesktop = useIsDesktop();
  const insRef = useRef<HTMLModElement>(null);

  // dev/목업 모드에서도 동일하게 push한다(mock SDK가 채움). 동작 일치로 버그 재현.
  useAdSensePush(insRef, isDesktop && !!slot);

  if (!slot || !isDesktop) {
    return null;
  }

  // 반응형 세로 디스플레이 광고(JCG-2194). 셸 사이드바 컬럼(좌 320~560 / 우 300~680px)에서
  // 고정 160px 스카이스크래퍼가 너무 작게 떠 비어 보이던 버그(JCG-2177/2178) 해결.
  // - 광고 박스 자체가 300px(하프페이지 세로 표준 폭) 고유 폭을 가져, 콘텐츠 sizing 컨테이너
  //   (클래식 레이아웃)에서도 ins(width:100%)가 0폭으로 붕괴하지 않는다. 광고 미노출(이용권/
  //   미로그인) 시엔 컴포넌트가 null 을 반환해 컬럼이 0폭으로 접힌다 → 빈 여백 예약 없음.
  // - max-w-full 로 컬럼이 300 미만(우측 RIGHT_MIN=300 경계 등)이면 넘치지 않게 캡. 셸의 넓은
  //   컬럼에서는 mx-auto 로 가로 중앙 정렬. AdSense 가 세로/하프페이지 포맷(160×600·300×600·
  //   300×250)을 고르도록 폭을 300 으로 유도(가로 배너 방지).
  // - minHeight 600px 로 로드 전 세로 자리를 예약해 CLS 회귀(JCG-399) 방지. 실제 자리표시
  //   배경은 투명(중립) — dev 목업 SDK 가 회색 박스를 직접 그린다(adsense-mock.ts).
  return (
    <div className="mx-auto w-[300px] max-w-full">
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", width: "100%", minHeight: "600px" }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
